import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { RegularSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import DownloadEntitiesButton from "@app/component/DownloadEntitiesModal/DownloadEntitiesButton";
import { StandardFilterButton } from "@app/component/filtering";
import QuickFilterBar from "@app/component/QuickFilterBar";
import useView from "@app/component/view/utils/useView";
import { exportActivities } from "@app/scene/activity/store/actions";
import { getTotalFilteredRecordsCount } from "@app/scene/activity/store/selectors";
import { isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getFavoriteSavedFilters } from "@app/store/savedFilters";
import layout from "@app/styles/layout";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";

import styles from "./FilterBar.module.scss";
import QuickActivityTypeFilter from "./QuickActivityTypeFilter";
import QuickAssigneeFilter from "./QuickAssigneeFilter";
import QuickSearch from "./QuickSearch";
import QuickSort from "./QuickSort";
import QuickStatusFilter from "./QuickStatusFilter";
import SettingsMenu from "./SettingsMenu";

interface Props {
  className?: string;
  exportActivities: typeof exportActivities;
  favoriteSavedFilters: RegularSavedFilter[];
  isMember: boolean;
  totalFilteredRecordsCount: number;
}

const FilterBar: React.FC<Props> = ({
  className,
  exportActivities,
  favoriteSavedFilters,
  isMember,
  totalFilteredRecordsCount,
}) => {
  const { viewState } = useView<Activity>();
  const intl = useIntl();

  const hiddenFilterFields = useMemo(
    () =>
      [
        activityFieldModel.getByName("quickFilter"),
        ...activityFieldModel.fields.filter((field) =>
          field.hasFeature(FieldFeature.NON_LIST_VIEW)
        ),
      ].filter(isDefined),
    []
  );

  const hiddenSavedFilterFields = useMemo(
    () => [activityFieldModel.getByName("quickFilter")].filter(isDefined),
    []
  );

  const handleDownload = useCallback(
    (columns: ColumnModel) => {
      exportActivities({ viewState: { ...viewState, columns } });
    },
    [exportActivities, viewState]
  );

  const useFilterBar = useMemo(() => {
    return (
      <>
        <StandardFilterButton
          context="activityPage"
          entityType={EntityType.ACTIVITY}
          hiddenFields={hiddenFilterFields}
          implicitFilterFields={hiddenSavedFilterFields}
        />
        <QuickFilterBar entityType={EntityType.ACTIVITY} savedFilters={favoriteSavedFilters} />
      </>
    );
  }, [favoriteSavedFilters, hiddenFilterFields, hiddenSavedFilterFields]);

  const icons = useMemo(
    () => (
      <Row gutter={layout.spacerS}>
        <Col>
          <SettingsMenu />
        </Col>
        <Col>
          <DownloadEntitiesButton
            entityType={EntityType.ACTIVITY}
            informationPoint={intl.formatMessage({
              id: "activityList.export.point1",
              defaultMessage: "All activities in view",
              description: "Information point 1 for export on activity list",
            })}
            onDownload={handleDownload}
            total={totalFilteredRecordsCount}
          />
        </Col>
      </Row>
    ),
    [handleDownload, intl, totalFilteredRecordsCount]
  );

  return (
    <>
      <div className={cn(className, styles.container)}>
        <div className={styles.left}>
          <QuickSearch />
          {favoriteSavedFilters.length <= 1 && useFilterBar}
        </div>
        <div className={styles.right}>
          <QuickActivityTypeFilter />
          {!isMember && <QuickAssigneeFilter />}
          <QuickStatusFilter />
          <QuickSort />
          {favoriteSavedFilters.length <= 1 && icons}
        </div>
      </div>
      <div>
        {favoriteSavedFilters.length > 1 && (
          <div className={cn(className, styles.container)}>
            <div className={styles.left}>{useFilterBar}</div>
            <div className={styles.right}>{icons}</div>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  favoriteSavedFilters: getFavoriteSavedFilters(state)(EntityType.ACTIVITY) as RegularSavedFilter[],
  isMember: isCurrentUserMember(state),
  totalFilteredRecordsCount: getTotalFilteredRecordsCount(state),
});

const mapDispatchToProps = {
  exportActivities,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterBar);
